import _ from 'lodash';
import { subDays, isAfter, isBefore, format as formatDate } from 'date-fns';
import Dygraph from 'dygraphs/index.es5';
import template from './graphPanel.html';


export default {
  name: 'graphPanel',
  template,
  controller: GraphPanelController,
  bindings: {
    panelId: '<'
  }
};

/* @ngInject */
function GraphPanelController(
  $scope,
  $timeout,
  $document,
  $q,
  $window,
  EquipmentInfoService,
  DashboardService,
  GraphPanelService,
  TotalFlowMeterService,
  PrettyNamesConst,
  ModalService
) {
  const vm = this;

  const millitimeout = 300;
  const inHalf = 2;
  const thirds = 3;
  const half = 180;
  const posRight = 90;
  const negRight = -90;
  const DEFAULT_DAYS_PULL = 7;
  const zoomRefetchDelay = 350;
  vm.headerMenu = [
    { text: 'Refresh data', do: refreshData },
    { text: 'Date Range', do: openDateRangeModal }
  ];

  vm.loadingSpinners = {
    stopGraphPanel: {}
  };
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.headerModel = {};
  vm.selectedOptions = {
    tanks: {},
    totalFlowMeters: {}
  };
  vm.graphVM = {};
  vm.graphVM.activeEquipment = {
    id: null,
    selectedOptions: {}
  };
  vm.currentReadings = {};
  vm.currentGroupings = {};
  vm.showOptionsCollapse = false;
  vm.sortReadingsDirection = 0;
  vm.annotationsEnabled = false;


  // private variables
  let eventWatcher;
  const activeEquipment = vm.graphVM.activeEquipment;
  let equipments;
  const dataPoints = {};
  const _equipmentGraphData = {};
  let minDateBookmark = null;
  let maxDateBookmark = null;
  const debouncedUpdateGraph = _.debounce(updateGraph, millitimeout);
  const debouncedZoomRefetch = _.debounce(zoomRefetch, zoomRefetchDelay);
  let lastSelectedOptions = {};
  // Per-equipment request bookkeeping so rapid zooms don't pile up: `inflightCanceler` aborts the
  // previous in-flight XHR, `requestGeneration` lets only the newest response rebuild the graph.
  const inflightCanceler = {};
  const requestGeneration = {};

  vm.$onDestroy = () => {
    _.forEach(_.keys(equipments || {}), disconnectChartResize);
    // deleteModel() takes no arguments — it resets the service's whole graph model.
    GraphPanelService.deleteModel();
  };
  vm.$onInit = checkPanelConfigurationState;
  vm.getPrettyName = getPrettyName;
  vm.lengthOf = lengthOf;
  vm.openTotalFlowModal = TotalFlowMeterService.openTotalFlowModal;
  vm.prioritizeThisItem = prioritizeThisItem;
  vm.resetZoom = resetZoom;
  vm.refreshData = refreshData;
  vm.graphHasData = graphHasData;
  vm.selectedAxis = selectedAxis;
  vm.setActiveEquipment = setActiveEquipment;
  vm.sortTheReadings = sortTheReadings;
  vm.toggleAnnotations = toggleAnnotations;
  vm.toggleOptionsCollapse = toggleOptionsCollapse;
  vm.toggleSelectedDataPointToGraph = toggleSelectedDataPointToGraph;
  vm.filterListByGrouping = filterListByGrouping;
  vm.toggleAll = toggleAll;
  vm.getGraphName = getGraphName;
  vm._processEvent = _processEvent;
  vm._updateDataPointsList = _updateDataPointsList;
  vm._equipmentGraphData = _equipmentGraphData;
  vm._graphCustomBars = graphCustomBars;
  vm._aggregatedDetailsEnabled = aggregatedDetailsEnabled;


  function checkPanelConfigurationState() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];
    if (vm.panel && vm.panel.equipment && vm.panel.equipment.length) {
      // panel is good to go
      vm.editPanel.needsToBeConfigured = false;
      init();
    } else {
      // panel needs to be configured
      activeEquipment.id = 'INACTIVE';
      vm.loadingSpinners.stopGraphPanel.INACTIVE = true;
      vm.editPanel.needsToBeConfigured = true;
    }
  }

  async function init() {
    vm.graphVM.equipments = GraphPanelService.getModel(vm.panel.id);
    equipments = vm.graphVM.equipments;

    const newHeaderModel = {};
    _.forEach(await EquipmentInfoService.getFullEquipment(vm.panel.equipment), (eq) => {
      newHeaderModel[eq.id] = eq;
      dataPoints[eq.id] = {
        all: [],
        groupings: _.get(vm.panel, ['config', eq.id, 'groupings'], {})
      };
      getFreshEventsForEquipment(eq)
        .then(() => {
          setEquipmentEventListener(eq.id);
          if (!activeEquipment.id) {
            setActiveEquipment(eq);
          }
        });
    });
    vm.headerModel = newHeaderModel;

    vm.annotationsEnabled = vm.panel.config.annotations;

    $scope.$on('panel-resized', () => {
      $timeout(() => {
        if (equipments[activeEquipment.id].chart) {
          equipments[activeEquipment.id].chart.resize();
        }
      }, millitimeout);
    });
  }

  function setEquipmentEventListener(equipmentId) {
    $scope.$on(`event:${equipmentId}`, _processEvent);
  }

  function _processEvent(__, eventObj) {
    const eventCopy = angular.copy(eventObj);

    const { listenForEvent, daysOfDataToPull = DEFAULT_DAYS_PULL } =
              _.get(vm.panel, ['config', eventCopy.equipment.id], {});

    const eventDate = new Date(eventCopy.eventDate);
    const end = vm.endRangeDate || new Date();
    const start = subDays(end, daysOfDataToPull);
    const afterStart = isAfter(eventDate, start);
    const beforeEnd = isBefore(eventDate, end);

    // if event is after start of range and before end, or no end is present, AND
    // if 'listenForEvent' is NOT specified for this equipment or if eventName matches listenForEvent, then
    // process the event
    if (
      (afterStart && (!vm.endRangeDate || (vm.endRangeDate && beforeEnd))) &&
        (_.isEmpty(listenForEvent) || (listenForEvent === eventCopy.eventName))
    ) {
      const hadDataBefore = _.get(vm.graphVM, ['equipments', eventCopy.equipment.id, 'hasData']) === true;
      GraphPanelService.updateModelFromEvent(vm.panel, eventCopy);
      vm._updateDataPointsList(eventCopy.equipment.id);
      const isActive = vm.graphVM.activeEquipment.id === eventCopy.equipment.id;
      const hasDataNow = _.get(vm.graphVM, ['equipments', eventCopy.equipment.id, 'hasData']) === true;
      if (isActive && !hadDataBefore && hasDataNow) {
        // First real reading for a panel that was showing the empty state (either no events at all, or
        // events with only null values). Re-run setActiveEquipment so the readings list and selected
        // options are rebuilt — not just the chart — and force a digest so the template swaps the empty
        // state for the graph even though this arrived from a socket handler outside the digest.
        const equipment = vm.headerModel[eventCopy.equipment.id];
        if (equipment) {
          $scope.$applyAsync(() => vm.setActiveEquipment(equipment));
          return;
        }
      }
      if (isActive && _.has(vm.graphVM, ['equipments', vm.graphVM.activeEquipment.id, 'chart'])) {
        debouncedUpdateGraph();
      }
    }
  }

  function lengthOf(object) {
    return _.size(object);
  }

  function getGraphName() {
    const metricName = _.get(vm.panel.config, [activeEquipment.id, 'metricName'], 'Levels');
    return `${_.get(vm.panel.config, [activeEquipment.id, 'displayName'],
      equipments[activeEquipment.id].equipment.name)} ${metricName}`;
  }

  function createGraph(data, daLabels, chosenColors, series) {
    // Options inherited from equipment config
    const {
      drawPoints = true,
      highlightCircleSize = 5,
      strokeWidth = 1,
      pointSize = 2,
      stepPlot = false
    } = _.get(vm.panel, ['config', vm.graphVM.activeEquipment.id], {});
    // customBars ([min,avg,max] band) is enabled either by panel config or whenever the currently
    // loaded data was downsampled server-side. Kept in sync with the data so the format always matches.
    const customBars = graphCustomBars(vm.graphVM.activeEquipment.id);
    const options = {
      title: `<div style="width: 90%; margin-left: 10%; text-align: left;">${vm.getGraphName()}</div>`,
      labels: daLabels,
      labelsDiv: 'graphLegendContainer',
      labelsSeparateLines: true,
      showRangeSelector: true, // enabled range selector, but turns off zoom select
      interactionModel: Dygraph.defaultInteractionModel, // turns zoom select back on
      connectSeparatedPoints: true,
      colors: chosenColors,
      fillGraph: false,
      rightGap: 50,
      customBars,
      axes: {
        x: {
          valueFormatter(xx) {
            return `<span class="graphLegendXAxes">${new Date(xx).toString()}</span>`;
          },
          axisLabelFormatter: Dygraph.dateAxisLabelFormatter,
          ticker: Dygraph.dateTicker
        },
        y: {
          drawAxis: true,
          drawHighlightPointCallback: getHighlightCallback(thirds, negRight),
          valueFormatter: formatAggregatedValues,
          labelsKMB: true
        },
        y2: {
          drawAxis: false,
          drawHighlightPointCallback: getHighlightCallback(thirds, posRight),
          valueFormatter: formatAggregatedValues,
          labelsKMB: true
        }
      },
      series,
      drawPoints,
      highlightCircleSize,
      strokeWidth,
      pointSize,
      stepPlot,
      stackedGraph: false,
      highlightCallback(event, __, [{ canvasx }]) {
        const activeId = activeEquipment.id;
        const offsetAmt = 20;
        if (canvasx < $document[0].getElementById(`panel-${vm.panel.id
        }-graphChart-${activeId}`).offsetWidth / inHalf) {
          $document[0].getElementById('graphLegendContainer').style.left = `${event.clientX + offsetAmt}px`;
          $document[0].getElementById('graphLegendContainer').style.top = `${event.clientY}px`;
        } else {
          $document[0].getElementById('graphLegendContainer').style.left =
              `${event.clientX - ($document[0].getElementById('graphLegendContainer').offsetWidth + offsetAmt)}px`;
          $document[0].getElementById('graphLegendContainer').style.top = `${event.clientY}px`;
        }
      },
      unhighlightCallback() {
        $document[0].getElementById('graphLegendContainer').style.left = '-999px';
        $document[0].getElementById('graphLegendContainer').style.top = '0px';
      },
      zoomCallback(minDate, maxDate) {
        minDateBookmark = minDate;
        maxDateBookmark = maxDate;
        // Drill down: refetch the zoomed range so the server returns it at a finer resolution.
        // The equipment is captured here, not read when the debounce fires, so a zoom can never be
        // applied to a different equipment the user switched to in the meantime.
        debouncedZoomRefetch(vm.graphVM.activeEquipment.id, minDate, maxDate);
      }
    };

    // restore the previous x position view if defined
    if (minDateBookmark && maxDateBookmark) {
      options.dateWindow = [minDateBookmark, maxDateBookmark];
    }

    /*
       * Merge the Dygraph Option pointClickCallback function into the options passed into Dygraphs.
       * Gated on the panel's performDataDownsampling CONFIG — not on `customBars` — because the
       * aggregated-details modal reads `data[point].points` (the per-second values carried only by
       * pre-aggregated `sensors/aggregated/*` events). Server-side downsampled buckets are a bare
       * [min, avg, max] with no `.points`, so offering the modal there would open it empty.
      */
    if (aggregatedDetailsEnabled(vm.graphVM.activeEquipment.id)) {
      options.pointClickCallback = (__, aggregatedPoint) => {
        // The Dygraph pointClickCallback option callback function takes the event
        // (i.e. mouse event) and the selected data point
        GraphPanelService.openAggregatedEventDetailsModal(
          aggregatedPoint,
          vm.graphVM.equipments[vm.graphVM.activeEquipment.id].events
        );
      };
    }

    const graphElementTarget = $document[0].getElementById(`panel-${vm.panel.id}-graphChart-${activeEquipment.id}`);
    const graph = new Dygraph(graphElementTarget, data, options);
    equipments[activeEquipment.id].chart = graph;
    // Remember the format the chart was built with so updateGraph can force a rebuild when it changes.
    equipments[activeEquipment.id]._customBarsMode = customBars;
    // Dygraph measures its container once, at creation — so a panel that is unsized or hidden at that
    // moment renders at ~0 width (the title wraps down the left edge and the plot is invisible). Fit it
    // on the next tick, and keep it fitted via a ResizeObserver so it recovers whenever the panel later
    // gets a real size (late flex/grid layout, becoming visible, drawer/tab reveal).
    $timeout(() => {
      if (_.get(equipments, [activeEquipment.id, 'chart']) === graph) {
        graph.resize();
      }
    });
    observeChartResize(activeEquipment.id, graphElementTarget, graph);
    return graph;
  }

  // Resize the chart whenever its container gets a real (non-zero) size. Guarded so a stale observer
  // (chart replaced/destroyed) does nothing.
  function observeChartResize(equipmentId, element, graph) {
    if (!$window.ResizeObserver) {
      return;
    }
    disconnectChartResize(equipmentId);
    const observer = new $window.ResizeObserver(() => {
      if (element.offsetWidth > 0 && _.get(equipments, [equipmentId, 'chart']) === graph) {
        graph.resize();
      }
    });
    observer.observe(element);
    equipments[equipmentId]._resizeObserver = observer;
  }

  function disconnectChartResize(equipmentId) {
    const observer = _.get(equipments, [equipmentId, '_resizeObserver']);
    if (observer) {
      observer.disconnect();
      equipments[equipmentId]._resizeObserver = null;
    }
  }

  // Rendering format: customBars is on when the panel is configured for it OR the loaded data is
  // server-downsampled — the chart must always match the shape of the data it was given.
  function graphCustomBars(equipmentId) {
    return Boolean(
      aggregatedDetailsEnabled(equipmentId) ||
        _.get(vm.graphVM, ['equipments', equipmentId, 'downsampled'], false)
    );
  }

  // Click-through to the aggregated-details modal. Deliberately NOT the same condition as
  // graphCustomBars: the modal reads `data[point].points`, the per-second values carried only by
  // pre-aggregated `sensors/aggregated/*` events. Server-downsampled buckets are a bare
  // [min, avg, max] with no `.points`, so enabling it there would open the modal empty.
  function aggregatedDetailsEnabled(equipmentId) {
    return Boolean(_.get(vm.panel, ['config', equipmentId, 'performDataDownsampling'], false));
  }

  function createGraphDataObj(eventObj) {
    const graphObj = [Date.parse(eventObj.eventDate)];
    // push chosen properties, sorted by name
    // TODO: Refactor
    _.forEach(_.sortBy(_.keys(activeEquipment.selectedOptions)), (option) => {
      const val = _.get(
        eventObj.data, [option, 'value'],
        _.get(eventObj.data, option, null)
      );
      graphObj.push(
        _.isBoolean(val) ? Number(val) : val
      );
    });
    return graphObj;
  }

  function updateGraph() {
    _equipmentGraphData[activeEquipment.id] = [];
    for (let i = 0, len = equipments[activeEquipment.id].events.length; i < len; i++) {
      _equipmentGraphData[activeEquipment.id]
        .push(createGraphDataObj(equipments[activeEquipment.id].events[i]));

    }


    // assign labels
    const daLabels = ['Date'];
    const seriesAxes = {};

    _.forEach(_.sortBy(_.keys(activeEquipment.selectedOptions)), (key) => {
      const prettyName = getPrettyName(key, activeEquipment.id);
      daLabels.push(prettyName);
      seriesAxes[prettyName] = {
        axis: _.get(vm.graphVM, ['activeEquipment', 'selectedOptions', key, 'yAxis'], 'y2')
      };
    });

    // assign colors
    const listOfAvailableColors = ['#0000FF', '#de6e14', '#18a689', '#b57ee0', '#c75757', '#0090d9'];
    const chosenColors = [];
    for (let j = 0, labelLen = _.size(activeEquipment.selectedOptions); j < labelLen; j++) {
      chosenColors.push(listOfAvailableColors[j % listOfAvailableColors.length]);
    }

    const { id: activeEq } = activeEquipment;
    const { [activeEq]: data } = _equipmentGraphData;
    // If the data format switched (raw <-> customBars) since the chart was built, updateOptions can't
    // reconcile it — destroy the chart so it is rebuilt below with the matching customBars setting.
    if (equipments[activeEq].chart && equipments[activeEq]._customBarsMode !== graphCustomBars(activeEq)) {
      disconnectChartResize(activeEq);
      try {
        equipments[activeEq].chart.destroy();
      } catch {
        _.noop();
      }
      equipments[activeEq].chart = null;
    }
    try {
      const graph = equipments[activeEq].chart;
      graph.updateOptions({
        file: data,
        labels: daLabels,
        series: seriesAxes,
        colors: chosenColors,
      });
    } catch {
      createGraph(data, daLabels, chosenColors, seriesAxes);
    }
    setAnnotations();
  }

  /**
   * Sorts the existing graph data into a series by day
   *
   * @private
   * @returns {object} object of date keys (YYMD)
   */
  function getDataByDay() {
    const dataByDay = {};
    _equipmentGraphData[activeEquipment.id].forEach((event) => {
      const date = formatDate(event[0], 'yyMd');
      dataByDay[date] ? dataByDay[date].push(event) : dataByDay[date] = [event];
    });
    return dataByDay;
  }

  /**
   * Toggles visibility of max-daily-value annotations
   */
  function toggleAnnotations() {
    vm.annotationsEnabled = !vm.annotationsEnabled;

    setAnnotations();
  }

  function setAnnotations() {
    const { chart: graph } = equipments[activeEquipment.id];

    if (!vm.annotationsEnabled) {
      return void graph.setAnnotations([]);
    }

    /** @type {Array<Array<object>>} maxReadingsForDay   Max values { value, when } for each reading selected */
    const maxReadingsForDay = _.map(getDataByDay(), readings =>
      _.reduce(readings, (maxValues, [when, ...reading]) => {
        // Record highest value of a given reading type (tracked by index)
        reading.forEach((val, idx) => {
          // On a downsampled window a reading is a [min, avg, max] customBars triple; annotate on its
          // max component. Anything non-numeric (null from a new device, a boolean series) is skipped —
          // comparing it would yield NaN and leave the seed in place, producing an annotation with no
          // `when` and a formatDate(undefined) RangeError that aborts the whole graph update.
          const numeric = _.isArray(val) ? _.last(val) : val;
          if (!_.isNumber(numeric)) {
            return;
          }
          const currMax = maxValues[idx];
          if (!currMax || numeric > currMax.value) {
            maxValues[idx] = { value: numeric, when };
          }
        });
        return maxValues;
        // One seed object per series: `_.constant({})` would share a single object across all indices.
      }, _.times(_.size(activeEquipment.selectedOptions), () => null)));

    const annotations = maxReadingsForDay.flatMap(
      dailyMaximums => dailyMaximums.map(
        (dailyMax, i) => _.isNumber(_.get(dailyMax, 'value')) && dailyMax.when ?
          {
            series: getPrettyName(_.keys(activeEquipment.selectedOptions)[i], activeEquipment.id),
            x: dailyMax.when,
            text: `${formatDate(dailyMax.when, 'MM-dd')} maximum: ${dailyMax.value}`,
            shortText: dailyMax.value.toFixed(vm.numberOfDigits),
            cssClass: 'annotation',
          } :
          // ! Sometimes newly-installed devices produce null values, so we discard those
          undefined
      )
    );
    graph.setAnnotations(annotations);
  }

  function setActiveEquipment(selectedEquipment) {
    if (eventWatcher) {
      eventWatcher();
      eventWatcher = null;
    }
    vm.selectedGrouping = null;
    activeEquipment.id = selectedEquipment.id;
    vm.numberOfDigits = vm.panel.config[vm.graphVM.activeEquipment.id].numberOfDigits ?? 4;
    vm.currentGroupings = _.get(dataPoints, [activeEquipment.id, 'groupings'], {});
    const equipmentModel = equipments[selectedEquipment.id];
    if (equipmentModel.events.length > 0) {
      // Make sure that the selected keys to graph are actually in the available options
      activeEquipment.selectedOptions =
          _.pick(activeEquipment.selectedOptions,
            _.keys(equipmentModel.latestReadings));
      // If graph by default is NOT setup
      if (_.isEmpty(_.get(vm.panel, ['config', equipmentModel.equipment.id, 'graphByDefault'], []))) {
        activeEquipment.selectedOptions = lastSelectedOptions; // Check what was checked previously
        lastSelectedOptions = activeEquipment.selectedOptions;
      } else {
        // If this equipment has a graphByDefault setup show the 'graphByDefault' ONLY
        if (_.isEmpty(lastSelectedOptions)) {
          // Store what was checked in the previous graph if we are coming from one with graphByDefault not setup
          lastSelectedOptions = activeEquipment.selectedOptions;
        }
        activeEquipment.selectedOptions = _.reduce(vm.panel.config[equipmentModel.equipment.id].graphByDefault,
          (acc, dataPointName) => {
            acc[dataPointName] = _.get(activeEquipment.selectedOptions, dataPointName, {
              yAxis: 'y2'
            });
            return acc;
          }, {});
      }

      filterListByGrouping();

      if (_.size(activeEquipment.selectedOptions)) {
        debouncedUpdateGraph();
      }
      vm.sortReadingsDirection = 2;
      sortTheReadings();
    } else {
      updateGraphAfterIncomingEvent(selectedEquipment);
    }
  }

  function updateGraphAfterIncomingEvent(selectedEquipment) {
    eventWatcher = $scope.$watch(() => vm.graphVM.equipments[vm.graphVM.activeEquipment.id].events.length,
      (len) => {
        if (len) {
          eventWatcher();
          setActiveEquipment(selectedEquipment);
        }
      });
  }

  function toggleSelectedDataPointToGraph(dataPointToAdd) {
    if (_.has(activeEquipment.selectedOptions, dataPointToAdd)) {
      // flush the debounce, in case there are any pending updateGraph calls before we remove the last selected item
      // to prevent _not_ calling the update, and then drawing all previously selected points on next toggle
      if (_.size(activeEquipment.selectedOptions) === 1) {
        debouncedUpdateGraph.flush();
      }
      delete activeEquipment.selectedOptions[dataPointToAdd];
      // delete vm.graphVM.selectedOptions[]
    } else {
      activeEquipment.selectedOptions[dataPointToAdd] = {
        yAxis: 'y2'
      };
    }

    if (_.size(activeEquipment.selectedOptions)) {
      debouncedUpdateGraph();
    }
  }

  function toggleOptionsCollapse() {
    vm.showOptionsCollapse = !vm.showOptionsCollapse;
  }

  /**
   * @param  {string} dataPointKey      Ugly key of data point
   * @param  {string} activeEquipmentId id of currently active equipment
   * @returns {string} Either pretty name or dataPointKey
   */
  function getPrettyName(dataPointKey, activeEquipmentId) {
    return _.get(
      equipments,
      [activeEquipmentId, 'equipment', 'settings', 'pointPrettyNames', dataPointKey],
      PrettyNamesConst[dataPointKey]
    ) ||
        dataPointKey;
  }

  function resetZoom() {
    minDateBookmark = null;
    maxDateBookmark = null;
    const eqId = activeEquipment.id;
    // If we drilled into a zoomed sub-range, the loaded data only covers that range — reload the
    // panel's full configured window first so the overview has data again. The view must be cleared
    // AFTER that reload: updateGraph reuses the existing chart via updateOptions (which doesn't touch
    // dateWindow), so without this the chart would stay visually zoomed on the old sub-range.
    if (vm.headerModel[eqId] && _.get(equipments, [eqId, '_zoomed'])) {
      const reload = reloadConfiguredWindow(eqId);
      if (reload && _.isFunction(reload.finally)) {
        reload.finally(() => clearChartWindow(eqId));
        return;
      }
    }
    clearChartWindow(eqId);
  }

  // Clear the chart's zoom window so it shows the full extent of the loaded data.
  function clearChartWindow(equipmentId) {
    if (_.get(equipments, [equipmentId, 'chart'])) {
      equipments[equipmentId].chart.updateOptions({
        dateWindow: null,
        valueRange: null
      });
    }
  }

  // True when the active panel has at least one finite reading loaded. Drives chart vs clean empty
  // state — an all-null "reporting but dead" panel shows the empty state instead of a degenerate chart.
  function graphHasData() {
    return _.get(vm.graphVM, ['equipments', activeEquipment.id, 'hasData']) === true;
  }

  // Re-pull the current window on demand (menu action) without reloading the whole dashboard.
  function refreshData() {
    return reloadConfiguredWindow(activeEquipment.id);
  }

  // Reload one equipment's full configured window and rebuild its graph.
  function reloadConfiguredWindow(eqId) {
    const equipment = vm.headerModel[eqId];
    if (!equipment) {
      return;
    }
    vm.loadingSpinners.stopGraphPanel[eqId] = false;
    return getFreshEventsForEquipment(equipment, vm.endRangeDate)
      .finally(() => setActiveEquipment(equipment));
  }

  // Refetch on zoom so the server returns the visible range at a finer resolution. Native double-click
  // unzoom also fires zoomCallback: if the chart is no longer zoomed and we had drilled in, reload the
  // full window instead. Only drills down while the loaded data is still downsampled.
  function zoomRefetch(zoomedEquipmentId, minDate, maxDate) {
    const eqId = zoomedEquipmentId;
    if (eqId !== activeEquipment.id) {
      return; // the user switched equipment after zooming — this range no longer applies
    }
    const chart = _.get(equipments, [eqId, 'chart']);
    if (chart && chart.isZoomed && !chart.isZoomed('x')) {
      if (_.get(equipments, [eqId, '_zoomed'])) {
        reloadConfiguredWindow(eqId);
      }
      return;
    }
    if (!_.get(equipments, [eqId, 'downsampled'])) {
      return;
    }
    const equipment = vm.headerModel[eqId];
    if (!equipment) {
      return;
    }
    vm.loadingSpinners.stopGraphPanel[eqId] = false;
    return getFreshEventsForRange(equipment, minDate, maxDate);
  }

  // Fetch an explicit [startMs, endMs] range, cancelling any in-flight request for the same equipment
  // and rebuilding only if this is still the latest request (drops out-of-order / superseded responses).
  // Start a request that supersedes any in-flight one for the same equipment: abort the previous XHR
  // and bump the generation so only the newest response is applied. Shared by the zoom drill-down and
  // the full-window reload, so either can supersede the other — otherwise a late zoom response could
  // overwrite a Reset Zoom / Refresh reload and re-mark the panel as zoomed.
  function beginGuardedFetch(equipmentId) {
    if (inflightCanceler[equipmentId]) {
      inflightCanceler[equipmentId].resolve('superseded');
    }
    const canceler = $q.defer();
    inflightCanceler[equipmentId] = canceler;
    requestGeneration[equipmentId] = (requestGeneration[equipmentId] || 0) + 1;
    return {
      canceler,
      generation: requestGeneration[equipmentId]
    };
  }

  function isCurrentFetch(equipmentId, generation) {
    return requestGeneration[equipmentId] === generation;
  }

  function settleGuardedFetch(equipmentId, generation) {
    if (isCurrentFetch(equipmentId, generation)) {
      inflightCanceler[equipmentId] = null;
      vm.loadingSpinners.stopGraphPanel[equipmentId] = true;
      $scope.$applyAsync();
    }
  }

  function getFreshEventsForRange(equipment, startMs, endMs) {
    const eqId = equipment.id;
    const { canceler, generation } = beginGuardedFetch(eqId);
    return GraphPanelService
      .getEventsForPeriodForEquipment(equipment, vm.panel, endMs, {
        startDateOverride: startMs,
        canceler: canceler.promise
      })
      .then((resolvedId) => {
        if (!isCurrentFetch(eqId, generation)) {
          return; // a newer request superseded this response — ignore it
        }
        _.set(equipments, [eqId, '_zoomed'], true);
        _updateDataPointsList(resolvedId);
        vm.setActiveEquipment(equipment);
      })
      .catch(_.noop)
      .finally(() => settleGuardedFetch(eqId, generation));
  }

  function selectedAxis(dataPointKey) {
    if (
      _.has(vm.graphVM, ['equipments', activeEquipment.id, 'chart']) &&
        _.has(activeEquipment.selectedOptions, dataPointKey)
    ) {
      const seriesName = vm.getPrettyName(dataPointKey, activeEquipment.id);
      equipments[activeEquipment.id].chart.updateOptions({
        series: _.set(
          {},
          [seriesName, 'axis'],
          _.get(activeEquipment.selectedOptions, [dataPointKey, 'yAxis'], 'y2')
        )
      });
    } else {
      updateGraph();
    }
  }

  function prioritizeThisItem(item) {
    const daReadings = vm.currentReadings[vm.graphVM.activeEquipment.id];
    const spliceInd = _.indexOf(daReadings, item);
    if (spliceInd > 0) {
      vm.isUnsorted = true;
      daReadings.splice(spliceInd, 1);
      daReadings.unshift(item);
    }
  }

  function sortTheReadings() {
    vm.isUnsorted = false;
    const sortBy = 2;
    const readings = vm.currentReadings[vm.graphVM.activeEquipment.id];
    if (vm.sortReadingsDirection === sortBy) {
      vm.sortReadingsDirection = -1;
      vm.currentReadings[vm.graphVM.activeEquipment.id] =
          _.sortBy(readings, rdId => vm.graphVM.activeEquipment.selectedOptions[rdId]);
    } else {
      readings.sort((aa, bb) => aa.localeCompare(bb));
      if (vm.sortReadingsDirection === 1) {
        readings.reverse();
      }
    }

    vm.sortReadingsDirection += 1;
  }

  function getHighlightCallback(sides, rotateShapeDegrees) {
    let rotationRadians = rotateShapeDegrees ? (rotateShapeDegrees / half) * Math.PI : 0;

    return function (__gg, __name, ctx, cx, cy, color, radius) {
      sides = sides || thirds;

      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      rotationRadians = rotationRadians || 0;
      const delta = Math.PI * inHalf / sides;

      ctx.beginPath();
      const initialAngle = rotationRadians;
      let angle = initialAngle;

      const computeCoordinates = function () {
        const xCor = cx + (Math.sin(angle) * radius);
        const yCor = cy + (-Math.cos(angle) * radius);
        return [xCor, yCor];
      };

      const initialCoordinates = computeCoordinates();
      const xx = initialCoordinates[0];
      const yy = initialCoordinates[1];
      ctx.moveTo(xx, yy);

      for (let idx = 0; idx < sides; idx++) {
        angle = idx === sides - 1 ? initialAngle : angle + delta;
        const [xCor, yCor] = computeCoordinates();
        ctx.lineTo(xCor, yCor);
      }
      ctx.fill();
      ctx.stroke();
    };
  }

  // TODO: Remove unused parameter and refactor usages
  function formatAggregatedValues(num, opts, seriesName, dygraph, row, col) {
    if (opts('customBars')) {
      return _.map(dygraph.getValue(row, col), val =>
        Math.abs(val) > 1 ?
          val.toFixed(2) :
          val.toFixed(4)
      ).join(', ');
    }
    if (
      (num === 1 || num === 0) &&
        _.isBoolean(_.get(equipments, [activeEquipment.id, 'latestReadings', seriesName]))
    ) {
      return Boolean(num);
    }
    return num.toFixed(vm.numberOfDigits);
  }

  async function openDateRangeModal() {
    const daysOfDataToPull = _.get(vm.panel, ['config', activeEquipment.id, 'daysOfDataToPull'], DEFAULT_DAYS_PULL);
    try {
      vm.endRangeDate = await ModalService.openDateRange(daysOfDataToPull, vm.endRangeDate);
      const equipment = vm.headerModel[activeEquipment.id];
      await getFreshEventsForEquipment(equipment, vm.endRangeDate);
      vm.setActiveEquipment(equipment);
    } catch {
      _.noop();
    }
  }

  function getFreshEventsForEquipment(equipment, endDate) {
    const eqId = equipment.id;
    const endMs = endDate && _.isDate(endDate) ? endDate.valueOf() : Date.now();
    const { canceler, generation } = beginGuardedFetch(eqId);
    return GraphPanelService
      .getEventsForPeriodForEquipment(equipment, vm.panel, endMs, { canceler: canceler.promise })
      .then((resolvedId) => {
        if (!isCurrentFetch(eqId, generation)) {
          return; // superseded (e.g. by a zoom drill-down) — don't apply a stale full-window result
        }
        // Loaded the full configured window (not a zoom drill-down) — clear the zoomed marker.
        _.set(equipments, [eqId, '_zoomed'], false);
        _updateDataPointsList(resolvedId);
      })
      .catch(_.noop)
      .finally(() => settleGuardedFetch(eqId, generation));
  }

  function _updateDataPointsList(eqId) {
    const all = dataPoints[eqId].all;
    const latestReadingsKeys = _.keys(equipments[eqId].latestReadings);
    _.pullAll(all, _.difference(all, latestReadingsKeys)); // remove points from list that no longer exists
    all.push(..._.difference(latestReadingsKeys, all)); // add points that are new
  }

  function filterListByGrouping() {
    let list = _.get(dataPoints, [activeEquipment.id, 'all'], []);
    if (vm.selectedGrouping) {
      list = _.get(vm.currentGroupings, [vm.selectedGrouping], list);
    }
    vm.currentReadings[activeEquipment.id] = list;
  }

  function toggleAll(select) {
    const list = vm.currentReadings[activeEquipment.id];
    const selected = _.keys(activeEquipment.selectedOptions);
    const toToggle = select ? _.difference(list, selected) : _.intersection(list, selected);
    _.map(toToggle, toggleSelectedDataPointToGraph);
  }
}
